import { useEffect, useRef, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { navItems, UTILITY_LINKS } from '@/config/nav'
import { site } from '@/config/site'
import { ButtonLink } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

interface Props {
  open: boolean
  onClose: () => void
  triggerRef: React.RefObject<HTMLButtonElement>
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function MobileMenu({ open, onClose, triggerRef }: Props) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    menuRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    return () => {
      document.body.style.overflow = originalOverflow
      triggerRef.current?.focus()
    }
  }, [open, triggerRef])

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab') return
    const focusables = menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
    if (!focusables || focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement as HTMLElement | null
    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  if (!open) return null

  return (
    <div
      ref={menuRef}
      id="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      onKeyDown={handleKeyDown}
      className={cn(
        'fixed inset-0 z-50 flex flex-col bg-ink-deep text-surface',
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-surface/10 px-4">
        <span className="eyebrow text-surface/70">Menu</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="inline-flex h-11 w-11 items-center justify-center hover:bg-surface/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface focus-visible:ring-offset-2 focus-visible:ring-offset-ink-deep"
        >
          <X className="h-6 w-6" aria-hidden />
        </button>
      </div>

      <nav
        aria-label="Primary mobile"
        className="flex-1 overflow-y-auto overscroll-contain px-6 py-6"
      >
        <ol className="space-y-5">
          {navItems.map((item, index) => (
            <li
              key={item.id}
              className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-4"
            >
              <span className="font-display text-xs font-light text-surface/40 tabular">
                {String(index + 1).padStart(2, '0')}
              </span>
              <a
                href={item.href}
                onClick={onClose}
                className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface focus-visible:ring-offset-4 focus-visible:ring-offset-ink-deep"
              >
                <span className="font-display text-2xl font-semibold leading-tight">
                  {item.label}
                </span>
                <span className="mt-1 block max-w-xs text-sm leading-snug text-surface/75">
                  {item.description}
                </span>
              </a>
            </li>
          ))}
        </ol>

        {/* The desktop utility bar's links (hidden below lg) live here. */}
        <ul className="mt-8 grid grid-cols-2 gap-x-4 border-t border-surface/10 pt-6">
          {UTILITY_LINKS.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                onClick={link.external ? undefined : onClose}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                className="flex min-h-11 items-center text-sm font-semibold uppercase tracking-[0.08em] text-surface/85 hover:text-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="space-y-3 border-t border-surface/10 px-6 py-5">
        <ButtonLink
          href={site.donateUrl}
          onClick={onClose}
          variant="inverse"
          size="lg"
          className="w-full"
        >
          Donate
        </ButtonLink>
        <a
          href={`mailto:${site.contactEmail}`}
          className="block text-center text-sm text-surface/75 hover:text-surface"
        >
          {site.contactEmail}
        </a>
      </div>
    </div>
  )
}
